import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  // Tooltip, // Tooltip was imported but not used in the snippet, removed for brevity
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV"; // Not used
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Not used
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import moment from "moment";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import ServicingApi from "../../services/AssociatedServices";
import TimePeriods from "../table/panel/TimePeriods";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Tooltip, IconButton, CircularProgress } from "@mui/material";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  pdf, // Use the programmatic `pdf` function
} from "@react-pdf/renderer";
import CrmPdfDocument from "./StandaloneCustomerPdfGenerator";
import translations from "../../assets/locals/translations";
import InventoryServices from "../../services/InventoryServices";

// Helper function to get the current financial year string (e.g., "2024-2025")
const getCurrentFinancialYearString = () => {
  const today = new Date();
  const currentMonth = today.getMonth(); // 0 (Jan) - 11 (Dec)
  let currentYear = today.getFullYear();
  if (currentMonth < 3) {
    // Before April (Jan, Feb, Mar)
    return `${currentYear - 1}-${currentYear}`;
  } else {
    // April onwards
    return `${currentYear}-${currentYear + 1}`;
  }
};

// Helper function to format a Date object to "YYYY-MM-DD" string
const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AllServicesData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const ServiceApi = new ServicingApi();
  const [stockData, setStockData] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [loadingPdfId, setLoadingPdfId] = useState(null);
  const InventoryService = new InventoryServices();
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedType, setSelectedType] = useState("All");
  const [pdfGenerate, setPdfGenerate] = useState({
    isGeneratePdf: false,
    pdfContent: null,
  });

  const [selectedYear, setSelectedYear] = useState(
    getCurrentFinancialYearString()
  );

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSizeKey = `rowsPerPage_${window.location.pathname}`;
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem(pageSizeKey);
    return saved ? parseInt(saved, 10) : 50;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [excelResult, setExcelResult] = useState([]);
  const fileInputRef = useRef(null);
  const isItlCustomerRoute = location.pathname.includes("itl-customer");

  const yearOptions = useMemo(() => {
    const options = [];
    const firstPossibleStartYear = 2012;
    const currentFYString = getCurrentFinancialYearString();
    const currentFYStartYear = parseInt(currentFYString.split("-")[0], 10);


    for (let y = currentFYStartYear; y >= firstPossibleStartYear; y--) {
      const financialYearLabel = `${y}-${y + 1}`;
      options.push({ value: financialYearLabel, label: financialYearLabel });
    }
    options.push({ value: "1998-2012", label: "1998-2012" });
    return options;
  }, []);

  const getAllDates = (dates) => {
    const [startDate, endDate] = dates.map((date) => new Date(date));
    const dateArr = [];
    const formatDate = (dateObj) => {
      // Renamed to avoid conflict with outer scope formatDate
      let year = dateObj.getFullYear();
      let month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
      let day = dateObj.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    let currentDate = new Date(startDate);
    if (endDate === undefined || startDate.getTime() === endDate.getTime()) {
      dateArr.push(formatDate(currentDate));
      return dateArr;
    } else {
      while (currentDate <= endDate) {
        dateArr.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dateArr;
    }
  };

  const getDeliveryLogs = async (
    currentPage,
    currentPageSize,
    datesFromTimePeriods,
    typeFromTimePeriods,
    search,
    financialYearString // e.g., "2024-2025"
  ) => {
    let startDateForApi = "";
    let endDateForApi = "";
    let yearForPayload; // The year to send in the payload (e.g., starting year of FY)

    const currentSystemFYString = getCurrentFinancialYearString();
    const todayFormatted = formatDateToYYYYMMDD(new Date());

    // Determine default start/end dates based on selected financial year
    let defaultFinancialYearStartDate = "";
    let defaultFinancialYearEndDate = "";

    if (financialYearString) {
      const [startFYStr, endFYStr] = financialYearString.split("-");
      const startFY = parseInt(startFYStr, 10);
      const endFY = parseInt(endFYStr, 10); // This is startFY + 1

      defaultFinancialYearStartDate = `${startFY}-04-01`;
      yearForPayload = startFY; // Send the starting year of the financial period

      // Check if the selected financial year is the current system's financial year
      if (financialYearString === currentSystemFYString) {
        // For the CURRENT financial year, end date is today
        defaultFinancialYearEndDate = todayFormatted;
      } else {
        // For PAST financial years, end date is March 31st of the next year
        defaultFinancialYearEndDate = `${endFY}-03-31`;
      }
    }

    if (typeFromTimePeriods === "All") {
      startDateForApi = defaultFinancialYearStartDate;
      endDateForApi = defaultFinancialYearEndDate;
    } else if (typeFromTimePeriods === "Custom") {
      if (
        Array.isArray(datesFromTimePeriods) &&
        datesFromTimePeriods.length > 0
      ) {
        const customDatesArray = getAllDates(datesFromTimePeriods);
        if (customDatesArray.length > 0) {
          startDateForApi = customDatesArray[0];
          endDateForApi =
            customDatesArray.length > 1
              ? customDatesArray[customDatesArray.length - 1]
              : customDatesArray[0];
        }
      }
      // Fallback for invalid custom dates
      if (!startDateForApi || !endDateForApi) {
        startDateForApi = defaultFinancialYearStartDate;
        endDateForApi = defaultFinancialYearEndDate;
        console.warn(
          "Custom type selected but dates processing failed or empty. Falling back to determined financial year range."
        );
      }
    } else {
      // "Today", "Yesterday", etc. from TimePeriods component
      if (
        Array.isArray(datesFromTimePeriods) &&
        datesFromTimePeriods.length > 0
      ) {
        // const specificDatesArray = getAllDates(datesFromTimePeriods);
        startDateForApi = datesFromTimePeriods[0];
        endDateForApi =
          datesFromTimePeriods.length > 1
            ? datesFromTimePeriods[datesFromTimePeriods.length - 1]
            : datesFromTimePeriods[0];
      }
      // Fallback for invalid specific dates
      if (!startDateForApi || !endDateForApi) {
        startDateForApi = defaultFinancialYearStartDate;
        endDateForApi = defaultFinancialYearEndDate;
        console.warn(
          `Type ${typeFromTimePeriods} selected but dates are invalid or empty. Falling back to determined financial year range.`
        );
      }
    }

    // Safety check: ensure start date is not after end date
    // This can happen if today is before April 1st and current FY is selected.
    // e.g. selected "2024-2025", today is "2024-03-15". startDateApi "2024-04-01", endDateApi "2024-03-15"
    // In such a case, the range is invalid. The API call might need to be skipped or dates adjusted.
    // For now, if start > end, we can make end = start, or not send dates.
    // However, with the current logic, defaultFinancialYearStartDate ("YYYY-04-01") and
    // defaultFinancialYearEndDate (today, or YYYY+1-03-31) should generally be valid.
    // The only issue is if 'today' is before 'YYYY-04-01' of the current FY start (e.g. it's March).
    // But `currentSystemFYString` logic handles this: if it's March 2025, current FY is 2024-2025.
    // So `defaultFinancialYearStartDate` would be "2024-04-01" and `endDate` "2025-03-current_day". This is fine.

    if (
      startDateForApi &&
      endDateForApi &&
      new Date(startDateForApi) > new Date(endDateForApi)
    ) {
      console.warn(
        `Calculated startDate (${startDateForApi}) is after endDate (${endDateForApi}). This might indicate an edge case. Review logic or API requirements. For now, setting endDate to startDate.`
      );
      endDateForApi = startDateForApi; // Or potentially send no date filters if this occurs
    }

    setLoading(true);
    try {
      const payload = {
        page: currentPage + 1,
        limit: currentPageSize,
        searchQuery: search,
      };
      if (yearForPayload !== undefined) {
        payload.year = yearForPayload;
      }

      if (!search || search.trim() === "") {
        if (startDateForApi && endDateForApi) {
          // Ensure both are valid before adding
          payload.startDate = startDateForApi;
          payload.endDate = endDateForApi;
        } else if (startDateForApi) {
          // If only startDate is meaningful (e.g. single day)
          payload.startDate = startDateForApi;
          payload.endDate = startDateForApi; // Or API might expect only startDate
        }
        // If startDateForApi/endDateForApi are empty, they won't be added.
      }

      // console.log("API Payload:", payload);
      const response = await ServiceApi.getITLServicesLog(payload);
      if (response && response.isSuccess) {
        setStockData(response.result || []);
        setTotalCount(response.totalCount || 0);
        setExcelResult(response.excelResult || []);
      } else {
        setStockData([]);
        setTotalCount(0);
        setExcelResult([]);
      }
    } catch (error) {
      console.error(error, "Error getting stocks list");
      setStockData([]);
      setTotalCount(0);
      setExcelResult([]);
      dispatch(
        setShowMessage({
          message: "Error fetching service logs.",
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };
  const handleSampleDownload = async () => {
    try {
      const response = await InventoryService.downloadSampleDelivery();
      if (response && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Delivery_Sample.xlsx");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading sample:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await InventoryService.uploadDeliveryExcel(formData);
      if (response && response.isSuccess) {
        dispatch(setShowMessage("Upload successful!"));
        getDeliveryLogs(
          page,
          pageSize,
          selectedDates,
          selectedType,
          searchQuery
        );
      } else {
        dispatch(setShowMessage(response?.message || "Upload failed"));
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setShowMessage("Error uploading file"));
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (page !== 0) setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    getDeliveryLogs(
      page,
      pageSize,
      selectedDates,
      selectedType,
      debouncedSearchQuery,
      selectedYear
    );
  }, [
    page,
    pageSize,
    selectedDates,
    selectedType,
    debouncedSearchQuery,
    selectedYear,
  ]);

  const handlePdf = async (data) => {
    try {
      const response = await ServiceApi.getPdfContentLogs({
        customer_id: data.id,
      });
      if (response.isSuccess && response.pdfContent) {
        setPdfGenerate({
          isGeneratePdf: true,
          pdfContent: response.pdfContent,
        });
      }
    } catch (error) {
      console.error("Failed to fetch PDF content:", error);
      setPdfGenerate({
        isGeneratePdf: false,
        pdfContent: null,
      });
    }
  };

  // This function now handles the entire flow:
  // fetch data -> generate blob -> open in new tab
  const handlePdfPreview = async (customer) => {
    if (loadingPdfId) return; // Prevent multiple clicks

    setLoadingPdfId(customer.id);

    try {
      // 1. Fetch the detailed data from the API
      const response = await ServiceApi.getPdfContentLogs({
        customer_id: customer.id,
      });

      if (response.isSuccess && response.pdfContent) {
        // 2. Generate the PDF as a blob in memory
        const blob = await pdf(
          <CrmPdfDocument customerData={response.pdfContent} />
        ).toBlob();

        // 3. Create a temporary URL for the blob
        const url = URL.createObjectURL(blob);

        // 4. Open the URL in a new tab
        window.open(url, "_blank");

        // Optional: Revoke the object URL after a short delay to free up memory
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (error) {
      console.error("Failed to generate or open PDF:", error);
      // You could show an error toast/snackbar to the user here
    } finally {
      // 5. Reset the loading state
      setLoadingPdfId(null);
    }
  };

  const columns = [
    {
      field: "customer_name",
      headerName: t.customerName || "Customer Name",
      flex: 1,
      minWidth: 310,
    },
    {
      field: "village",
      headerName: t.village || "Village",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "taluka",
      headerName: t.taluka || "Taluka",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "phone_number",
      headerName: t.phoneNumber || "Phone Number",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "delivery_date",
      headerName: t.deliveryDate || "Delivery Date",
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    // { field: "manufacturer_name", headerName: "Manufacture", flex: 1, minWidth: 150 },
    { field: "new_modal", headerName: t.model || "Model", flex: 1, minWidth: 150 },
    {
      field: "chassis_number",
      headerName: t.chassisNo || "CHASSIS NO",
      flex: 1,
      minWidth: 200,
    },
    { field: "engine_number", headerName: t.engineNo || "ENGINE NO", flex: 1, minWidth: 200 },
  ];

  const excelColumns = [
    { field: "customer_name", headerName: t.customerName || "Customer Name" },
    { field: "village", headerName: t.village || "Village" },
    { field: "taluka", headerName: t.taluka || "Taluka" },
    { field: "phone_number", headerName: t.phoneNumber || "Phone Number" },
    { field: "delivery_date", headerName: t.deliveryDate || "Delivery Date" },
    { field: "manufacturer_name", headerName: t.manufacture || "Manufacture" },
    { field: "new_modal", headerName: t.model || "Model" },
    { field: "chassis_number", headerName: t.chassisNumber || "Chassis No" },
    { field: "engine_number", headerName: t.engineNumber || "Engine No" },
  ];

  const exportToCSV = (columnsToExport, dataToExport) => {
    const headers = columnsToExport.map((col) => col.headerName);
    const rows = dataToExport.map((row) =>
      columnsToExport.map((col) => {
        const raw = row[col.field];
        if (
          raw != null &&
          (col.field === "date" ||
            col.field === "delivery_date" ||
            col.field === "enquiry_date" ||
            col.field === "retail_date")
        ) {
          const m = moment(raw);
          return m.isValid() ? m.format("DD-MM-YYYY") : "";
        }
        if (raw != null) {
          return String(raw).replace(/"/g, '""');
        }
        return "";
      })
    );
    const csvLines = [
      headers.join(","),
      ...rows.map((cols) => `"${cols.join('","')}"`),
    ];
    const csvContent = csvLines.join("\n");
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const fileName = `SERVICE-DELIVERY-${selectedType}-${selectedYear}-${yyyy}-${mm}-${dd}.csv`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApplyButton = (dates, type) => {
    setSelectedType(type);
    setSelectedDates(dates);
    if (page !== 0) setPage(0);
  };

  const handleYearChange = (event) => {
    const newYear = event.target.value;
    setSelectedYear(newYear);
    if (page !== 0) setPage(0);
  };

  const handlePaginationModelChange = (newModel) => {
    setPage(newModel.page);
    setPageSize(newModel.pageSize);
    try { sessionStorage.setItem(pageSizeKey, String(newModel.pageSize)); } catch (e) { }
  };

  return (
    <div className="bg-white p-2">
      {isItlCustomerRoute && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {t.itlcustomer || "ITL Customer"}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          mb: 1,
          gap: isMobile ? 1 : 0,
        }}
      >
        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300 }}
        />
        <div className="d-flex flex-wrap justify-content-end align-items-center ms-md-1">
          <FormControl
            size="small"
            sx={{ minWidth: 120, mr: 1, mb: isMobile ? 1 : 0 }}
          >
            <InputLabel id="year-select-label">{t.financialYear || "Financial Year"}</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label={t.financialYear || "Financial Year"}
              onChange={handleYearChange}
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TimePeriods handleApplyButton={handleApplyButton} />
          <button
            className="btn btn-outline-info d-flex align-items-center ms-1"
            onClick={handleSampleDownload}
            title="Download Sample Format"
          >
            Sample
          </button>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
          <button
            className="btn btn-outline-success d-flex align-items-center ms-1"
            onClick={() => fileInputRef.current.click()}
            title="Upload Excel File"
          >
            Upload
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center ms-1"
            onClick={() => exportToCSV(excelColumns, excelResult)}
            disabled={excelResult.length === 0}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-download me-2"
              viewBox="0 0 16 16"
            >
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3a.5.5 0 0 1 1 0v3a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-3a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 1 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
            {t.download || "Download"}
          </button>
        </div>
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 120px)" }}
      >
        <DataGrid
          rows={stockData}
          columns={[
            {
              field: "menu",
              headerName: t.menu || "Menu",
              sortable: false,
              filterable: false,
              width: 60,
              align: "left",
              headerAlign: "left",
              renderCell: (params) => {
                const isLoading = loadingPdfId === params.row.id;
                return (
                  <Tooltip title={t.pdfPreview || "PDF Preview"} arrow>
                    {/* Disable button while loading to prevent multiple clicks */}
                    <IconButton
                      onClick={() => handlePdfPreview(params.row)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <PictureAsPdfIcon fontSize="small" color="error" />
                      )}
                    </IconButton>
                  </Tooltip>
                );
              },
            },
            {
              field: "s_no",
              headerName: t.no || "No",
              width: 70,
              sortable: false,
              filterable: false,
              renderCell: (params) => {
                return (
                  page * pageSize +
                  params.api.getRowIndexRelativeToVisibleRows(params.row.id) +
                  1
                );
              },
            },
            ...columns,
          ]}
          getRowId={(row) => row.id}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          loading={loading}
          pageSizeOptions={[10, 15, 25, 50, 100]}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSelectionOnClick
          autoHeight={isMobile}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: false,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{
            width: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-toolbarContainer": {
              justifyContent: "flex-end",
            },
          }}
        />
      </div>
    </div>
  );
};

export default AllServicesData;
